import { FC, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ConfirmationStep } from "../../components/confirmationStep/ConfirmationStep";
import { Layout } from "../../containers/layout";
import { useTranslation } from "../../hooks/useTranslationHook/useTranslation";
import { AcceptMoveStep } from "../../components/acceptMoveStep/AcceptMoveStep";
import {
  useInviteExistingUserFlow,
  InviteExistingUserStepEnum,
  InviteExistingUserFlowProvider,
  ExistingInviteVariant
} from "../../contexts/inviteExistingUserContext";
import { AcceptInvitationStep } from "../../components/acceptInvitationStep/AcceptInvitationStep";
import { useInvitationInformation } from "../../hooks/useInvitationInformation/useInvitationInformation";
import { useInvitationChoice } from "../../hooks/useInvitationChoice/useInvitationChoice";
import { useToastNotification } from "../../hooks/useToastNotification/useToastNotification";
import { useExistingPrimaryInvitationInformation } from "../../hooks/useExistingPrimaryInvitationInformation/useExistingPrimaryInvitationInformation";
import { navigateSecurely } from "../../utils/navigation/navigationUtils";

export const InviteExistingUserPage: FC = () => {
  const { translateString } = useTranslation();
  const { workflowId } = useParams<{ workflowId: string }>();

  const {
    currentStep,
    variant,
    setOrganization,
    setCurrentOrganization,
    setCurrentStep,
    setMoveResult,
    setUserEmail,
    resetFlow,
    invitationState,
    setInvitationState,
    moveResult
  } = useInviteExistingUserFlow();

  const { getInvitationInformationSafely } = useInvitationInformation();
  const { getExistingPrimaryInvitationInformationSafely } =
    useExistingPrimaryInvitationInformation();
  const { sendInvitationChoice } = useInvitationChoice();
  const { showNotification, NotificationElement } = useToastNotification();

  const [initializing, setInitializing] = useState(true);
  const [externalOrgName, setExternalOrgName] = useState<string>("—");

  useEffect(() => {
    const init = async () => {
      resetFlow();

      if (!workflowId) {
        showNotification("error_invalid_invitation", "error");
        setInitializing(false);
        return;
      }

      if (variant === "external") {
        const info = await getInvitationInformationSafely(workflowId);
        if (!info) {
          setInitializing(false);
          return;
        }
        setExternalOrgName(info.organizationName);
        setInvitationState(info.invitationState);
      } else {
        const info = await getExistingPrimaryInvitationInformationSafely(workflowId);
        if (!info) {
          setInitializing(false);
          return;
        }
        setUserEmail(info.email);
        setOrganization(info.organizationName);
        setCurrentOrganization(info.originalOrganizationName);
        setInvitationState(info.invitationState);
      }

      setInitializing(false);
    };

    void init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId, variant]);

  const handleLogin = () => {
    navigateSecurely();
  };

  const handleAcceptExternal = async () => {
    if (!workflowId) return;
    const res = await sendInvitationChoice(workflowId, "accepted");
    if (!res.ok) {
      showNotification(
        res.code ? translateString(res.code as any) : (res.message ?? "Error"),
        "error"
      );
      return;
    }
    setMoveResult("accepted");
    setCurrentStep(InviteExistingUserStepEnum.ConfirmationStep);
  };

  const handleRejectExternal = async () => {
    if (!workflowId) return;
    const res = await sendInvitationChoice(workflowId, "rejected");
    if (!res.ok) {
      showNotification(
        res.code ? translateString(res.code as any) : (res.message ?? "Error"),
        "error"
      );
      return;
    }
    setMoveResult("rejected");
    setCurrentStep(InviteExistingUserStepEnum.ConfirmationStep);
  };

  const renderAcceptanceStep = () => {
    return variant === "primary" ? (
      <AcceptMoveStep />
    ) : (
      <AcceptInvitationStep
        organizationName={externalOrgName}
        relationType={variant}
        invitationState={invitationState || "-"}
        onAccept={handleAcceptExternal}
        onReject={handleRejectExternal}
      />
    );
  };

  const renderStep = () => {
    switch (currentStep) {
      case InviteExistingUserStepEnum.AcceptanceStep:
        return renderAcceptanceStep();

      case InviteExistingUserStepEnum.ConfirmationStep: {
        const isAccepted = moveResult === "accepted";

        const cardTitle =
          variant === "primary"
            ? translateString(
                isAccepted ? "move_organization_accepted" : "move_organization_rejected"
              )
            : translateString(isAccepted ? "invitation_accepted" : "invitation_rejected");

        const message =
          variant === "primary"
            ? translateString(
                isAccepted
                  ? "move_organization_description"
                  : "move_organization_rejected_description"
              )
            : translateString(
                isAccepted
                  ? "external_invitation_accepted_description"
                  : "invitation_rejected_description"
              );

        return (
          <ConfirmationStep
            cardTitle={cardTitle}
            message={message}
            buttonText={translateString("return_to_login")}
            onButtonClick={handleLogin}
          />
        );
      }

      default:
        return null;
    }
  };

  return (
    <Layout>
      {initializing ? null : (
        <>
          {renderStep()}
          {NotificationElement}
        </>
      )}
    </Layout>
  );
};

export const InviteExistingUserPageWithProvider: FC<{ variant: ExistingInviteVariant }> = ({
  variant
}) => (
  <InviteExistingUserFlowProvider initialVariant={variant}>
    <InviteExistingUserPage />
  </InviteExistingUserFlowProvider>
);
